import { useId, type HTMLAttributes, type ReactNode } from "react";
import { cx } from "./cx";
import "./Card.css";

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title?: ReactNode;
  /** Right-aligned header content (buttons, badges). */
  actions?: ReactNode;
  footer?: ReactNode;
  /** Body padding. Use "none" for flush tables. Default "md". */
  padding?: "none" | "sm" | "md";
  /** Heading level for the title. Default 2. */
  headingLevel?: 2 | 3 | 4;
  /** Accent edge for tiles that report a state (pair with text, never colour alone). */
  tone?: "default" | "success" | "warning" | "danger";
}

/** Raised surface with optional header (title + actions) and footer. Renders a <section>. */
export function Card({
  title,
  actions,
  footer,
  padding = "md",
  headingLevel = 2,
  tone = "default",
  className,
  children,
  ...rest
}: CardProps) {
  const auto = useId();
  const titleId = title ? `card${auto}` : undefined;
  const Heading = `h${headingLevel}` as const;
  return (
    <section
      className={cx("ui-card", `ui-card--tone-${tone}`, className)}
      aria-labelledby={titleId}
      {...rest}
    >
      {title || actions ? (
        <header className="ui-card__header">
          {title ? (
            <Heading className="ui-card__title" id={titleId}>
              {title}
            </Heading>
          ) : (
            <span />
          )}
          {actions ? <div className="ui-card__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cx("ui-card__body", `ui-card__body--${padding}`)}>{children}</div>
      {footer ? <footer className="ui-card__footer">{footer}</footer> : null}
    </section>
  );
}
